import React, { useEffect, useRef, useState } from "react";
import tottiesLogo from "../img/totties_text.svg";
import { useAuth } from "./AuthProvider";
import { isApiConfigured } from "../utils/api";
import "./Header.css";

interface HeaderProps {
  onTabChange: (tab: string) => void;
  activeTab: string;
  onOpenAuth: () => void;
  onOpenSavedModal?: () => void;
  onRestoreCalculation?: (type: import("../utils/api").CalculationType, payload: Record<string, unknown>) => void;
}

const Header: React.FC<HeaderProps> = ({
  onTabChange,
  activeTab,
  onOpenAuth,
  onOpenSavedModal,
  onRestoreCalculation,
}) => {
  const { user, loading, signOut } = useAuth();
  const [theme, setTheme] = useState<string>(
    typeof window !== "undefined"
      ? (localStorage.getItem("theme") as string) || "system"
      : "system"
  );
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);

  // Close avatar dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const prefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
    const isDark = theme === "dark" || (theme === "system" && prefersDark);
    if (isDark) {
      root.setAttribute("data-theme", "dark");
    } else {
      root.setAttribute("data-theme", "light");
    }
  }, [theme]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, tab: string) => {
    e.preventDefault();
    onTabChange(tab);
  };

  const cycleTheme = () => {
    const next = theme === "light" ? "dark" : theme === "dark" ? "system" : "light";
    setTheme(next);
    localStorage.setItem("theme", next);
  };

  return (
    <>
      <header className="app-header">
      <div className="header-container">
        <a
          href="https://totties.co.za"
          className="logo-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={tottiesLogo} alt="Totties Logo" className="logo" />
        </a>

        <nav className="nav-menu">
          <a
            href="#home"
            className={`nav-item ${activeTab === "#home" ? "active" : ""}`}
            onClick={(e) => handleClick(e, "#home")}
          >
            Home
          </a>
          <a
            href="#affordability"
            className={`nav-item ${
              activeTab === "#affordability" ? "active" : ""
            }`}
            onClick={(e) => handleClick(e, "#affordability")}
          >
            Affordability
          </a>
          <a
            href="#monthlyfinances"
            className={`nav-item ${activeTab === "#monthlyfinances" ? "active" : ""}`}
            onClick={(e) => handleClick(e, "#monthlyfinances")}
          >
            Monthly Finances
          </a>
          <a
            href="#tax"
            className={`nav-item ${activeTab === "#tax" ? "active" : ""}`}
            onClick={(e) => handleClick(e, "#tax")}
          >
            Tax
          </a>

          {/* Desktop auth — hidden on mobile */}
          {!loading && (
            <div className="header-auth header-auth-desktop">
              {user ? (
                <>
                  {isApiConfigured() && onOpenSavedModal && (
                    <button
                      type="button"
                      className="nav-item header-saved-btn"
                      onClick={onOpenSavedModal}
                      aria-haspopup="dialog"
                    >
                      Saved
                    </button>
                  )}
                  <span className="header-user">{user.email ?? user.username}</span>
                  <button
                    type="button"
                    className="nav-item header-signout"
                    onClick={() => signOut()}
                    title="Sign out"
                    aria-label="Sign out"
                  >
                    <span className="header-signout-icon" aria-hidden="true">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                    </span>
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="nav-item header-signin"
                  onClick={onOpenAuth}
                >
                  Sign in
                </button>
              )}
            </div>
          )}

          {/* Mobile avatar — hidden on desktop */}
          {!loading && (
            <div className="header-avatar-wrap" ref={avatarRef}>
              <button
                type="button"
                className="header-avatar-btn"
                onClick={() => setAvatarOpen((o) => !o)}
                aria-label="Account menu"
                aria-expanded={avatarOpen}
              >
                {user ? (
                  <span className="header-avatar-initials">
                    {(user.email ?? user.username ?? "?")[0].toUpperCase()}
                  </span>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                  </svg>
                )}
              </button>

              {avatarOpen && (
                <div className="header-avatar-dropdown" role="menu">
                  {user ? (
                    <>
                      <span className="avatar-dropdown-email">{user.email ?? user.username}</span>
                      {isApiConfigured() && onOpenSavedModal && (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setAvatarOpen(false); onOpenSavedModal(); }}
                        >
                          Saved calculations
                        </button>
                      )}
                      <button
                        type="button"
                        role="menuitem"
                        className="avatar-dropdown-signout"
                        onClick={() => { setAvatarOpen(false); signOut(); }}
                      >
                        Sign out
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setAvatarOpen(false); onOpenAuth(); }}
                    >
                      Sign in
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </nav>
      </div>
    </header>

      <button
        type="button"
        className="theme-fab"
        onClick={cycleTheme}
        aria-label={`Theme: ${theme}`}
        title={theme === "dark" ? "Dark mode" : theme === "light" ? "Light mode" : "System theme"}
      >
        {theme === "dark" ? "🌙" : theme === "light" ? "☀️" : "🖥️"}
      </button>
    </>
  );
};

export default Header;
